import { createContext, useContext, useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";

export const FONTS = ["Plus Jakarta Sans", "Manrope", "DM Sans", "Outfit", "Figtree", "Sora", "IBM Plex Sans", "Nunito Sans"];
const LayoutCtx = createContext(null);

const DARK = { surface: "#141513", surface_alt: "#1F211E", text: "#ECECE7", border: "#2E312D", muted: "#9A9C95" };

function mix(hex, amt) {
  const h = (hex || "#000000").replace("#", "");
  const n = parseInt(h.length === 3 ? h.split("").map((c) => c + c).join("") : h, 16);
  const ch = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map((c) => Math.round(c + (255 - c) * amt));
  return `rgb(${ch.join(",")})`;
}

function isDark(theme) {
  if (theme === "dark") return true;
  if (theme === "auto") return window.matchMedia?.("(prefers-color-scheme: dark)").matches;
  return false;
}

export function applyLayout(l) {
  if (!l) return;
  const r = document.documentElement.style;
  const dark = isDark(l.theme);
  const pal = dark ? DARK : { surface: l.surface, surface_alt: l.surface_alt, text: l.text, border: "#E5E5E2", muted: "#8A8A85" };
  r.setProperty("--brand", l.brand);
  r.setProperty("--brand-soft", dark ? "rgba(255,255,255,0.06)" : mix(l.brand, 0.86));
  r.setProperty("--brand-tint", dark ? "rgba(255,255,255,0.03)" : mix(l.brand, 0.93));
  r.setProperty("--accent", l.accent);
  r.setProperty("--accent-soft", dark ? "rgba(199,125,32,0.15)" : mix(l.accent, 0.85));
  r.setProperty("--surface", pal.surface);
  r.setProperty("--surface-alt", pal.surface_alt);
  r.setProperty("--text", pal.text);
  r.setProperty("--border", pal.border);
  r.setProperty("--muted", pal.muted);
  r.setProperty("--radius", `${l.radius}px`);
  r.setProperty("--pad", l.density === "compact" ? "12px" : "18px");
  r.setProperty("--font", `"${l.font}", system-ui, sans-serif`);
  document.documentElement.classList.toggle("theme-dark", !!dark);
  const id = "layout-font";
  let link = document.getElementById(id);
  if (!link) {
    link = document.createElement("link");
    link.id = id;
    link.rel = "stylesheet";
    document.head.appendChild(link);
  }
  link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(l.font)}:wght@400;500;600;700;800&display=swap`;
  document.title = l.app_name || "Relatório CMV";
  setFavicon(l);
}

function setFavicon(l) {
  const initial = ((l.app_name || "C").trim()[0] || "C").toUpperCase().replace(/[<&>"]/g, "");
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64"><rect width="64" height="64" rx="14" fill="${l.brand || "#4E6E5D"}"/><text x="32" y="44" font-family="Arial,sans-serif" font-size="36" font-weight="700" fill="#fff" text-anchor="middle">${initial}</text></svg>`;
  const href = l.logo_url || `data:image/svg+xml,${encodeURIComponent(svg)}`;
  for (const rel of ["icon", "apple-touch-icon"]) {
    let link = document.querySelector(`link[rel="${rel}"]`);
    if (!link) {
      link = document.createElement("link");
      link.rel = rel;
      document.head.appendChild(link);
    }
    if (link.href !== href) link.href = href;
    link.setAttribute("data-testid", `favicon-${rel}`);
  }
}

export function LayoutProvider({ children }) {
  const { data, refetch } = useQuery({ queryKey: ["layout"], queryFn: () => api("/layout"), staleTime: Infinity });
  const [preview, setPreview] = useState(null);
  const layout = preview || data;
  useEffect(() => applyLayout(layout), [layout]);
  return <LayoutCtx.Provider value={{ layout, setPreview, refetch }}>{children}</LayoutCtx.Provider>;
}

export const useLayout = () => useContext(LayoutCtx);
